// WHAT IS IT FOR, AND WHERE WILL YOU POST IT? (owner 2026-10-23: two kinds of buyer, "split it that way": an artist
// promoting a song, whose videos come in the size of every place they post, and someone who just wants a moving
// picture, a gift, a memorial, for fun, in their picture's own shape or any they pick.)
// (owner 2026-10-23: the first question is where it goes, with the reason shown, because
// people distrust a question with no reason; and the picture is never cut.) The buyer picks every place they will use
// it; each place's shape is shown with their picture in it, as the cover will be made: filled around where the shape
// is wider or taller than the picture (the server's extend.js), or, when they choose, cut to a window they drag.
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Heart, Loader2, Move, Music2, Wand2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FramePreview } from '@/lib/quicktools-api';
import { FREE_SHAPES, PLATFORM_KEYS, PLATFORM_SHAPES, ratioLabel, sizeOf, type Place, type PlatformKey, type ShapeKey } from '@/lib/cover-shapes';

export type CoverPurpose = 'artist' | 'picture';
export interface CoverShapesProps {
  /** Promoting a song (the platforms) or just a moving picture (shapes picked freely); '' until chosen. */
  purpose: CoverPurpose | '';
  onPurpose: (v: CoverPurpose) => void;
  platforms: string[];
  free: string[];
  onPlatforms: (v: string[]) => void;
  onFree: (v: string[]) => void;
  /** The shapes made (main first) and those the option bought does not include. */
  shapes: ShapeKey[];
  dropped: ShapeKey[];
  /** The picked picture: its URL and size. */
  picture: { url: string; w: number; h: number } | null;
  previews: FramePreview['shapes'] | null;
  busy: boolean;
  places: Partial<Record<ShapeKey, Place>>;
  onPlace: (key: ShapeKey, place: Place | null) => void;
  /** The prices this step shows (owner 2026-10-23): just a moving picture's flat price; the platforms free, each one past
   *  them, and how many past them are ticked with their total. */
  prices: { picture: string; free: number; each: string; extra: number; extraTotal: string };
}

export function CoverShapes({ purpose, onPurpose, platforms, free, onPlatforms, onFree, shapes, dropped, picture, previews, busy, places, onPlace, prices }: CoverShapesProps) {
  const { t } = useTranslation();
  const k = (key: string, opts?: Record<string, unknown>) => t(`qt.tools.canvas.shapes.${key}`, opts);
  const toggle = (p: string) => onPlatforms(platforms.includes(p) ? platforms.filter((x) => x !== p) : [...platforms, p]);
  // Just a moving picture is one version (owner 2026-10-23: "the average person wouldnt need size specific requirements
  // and multiple versions"): its own shape, or one other shape instead.
  const pickFree = (s: string) => onFree(s === 'orig' ? [] : [s]);
  // Which of the buyer's places each shape is for (the tile's label).
  const usedBy = (s: ShapeKey) => (platforms as PlatformKey[]).filter((p) => (PLATFORM_KEYS as string[]).includes(p) && PLATFORM_SHAPES[p].includes(s)).map((p) => k(`platform.${p}`));
  return (
    <div className="space-y-4" data-testid="qt-shapes">
      <div className="grid grid-cols-2 gap-2">
        {([['artist', Music2], ['picture', Heart]] as const).map(([id, Icon]) => {
          const on = purpose === id;
          return (
            <button key={id} type="button" onClick={() => onPurpose(id)} aria-pressed={on} data-testid={`qt-purpose-${id}`}
              className={cn('flex min-h-[84px] flex-col items-start justify-center gap-1 rounded-2xl border px-3 py-2.5 text-left transition-colors',
                on ? 'border-primary bg-primary/15 text-foreground' : 'border-white/10 bg-white/[0.04] text-foreground/85 hover:bg-white/[0.08]')}>
              <Icon className={cn('h-5 w-5', on ? 'text-primary' : 'text-foreground/70')} aria-hidden />
              <span className="text-[14.5px] font-bold leading-tight">{k(`purpose.${id}`)}</span>
              <span className="text-[11.5px] leading-snug text-muted-foreground">{k(`purpose.${id}Hint`, { price: prices.picture })}</span>
            </button>
          );
        })}
      </div>

      {purpose === 'artist' && (
        <div>
          <p className="text-[13px] font-semibold text-foreground/85">{k('where')}</p>
          <p className="mt-0.5 text-[12px] text-muted-foreground" data-testid="qt-platform-price">
            {k('platformPrice', { included: prices.free, each: prices.each })}
            {prices.extra > 0 && <span className="font-semibold text-primary"> · {k('platformExtra', { count: prices.extra, total: prices.extraTotal })}</span>}
          </p>
        </div>
      )}
      {purpose === 'artist' && (
      <div className="grid grid-cols-2 gap-2">
        {PLATFORM_KEYS.filter((p) => p !== 'me').map((p) => {
          const on = platforms.includes(p);
          return (
            <button
              key={p}
              type="button"
              onClick={() => toggle(p)}
              aria-pressed={on}
              data-testid={`qt-platform-${p}`}
              className={cn('flex min-h-[52px] items-center gap-2 rounded-2xl border px-3 py-2 text-left transition-colors',
                on ? 'border-primary bg-primary/15 text-foreground' : 'border-white/10 bg-white/[0.04] text-foreground/85 hover:bg-white/[0.08]')}
            >
              <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded-md border', on ? 'border-primary bg-primary text-[#10131a]' : 'border-white/25')}>
                {on && <Check className="h-3.5 w-3.5" aria-hidden />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-semibold leading-tight">{k(`platform.${p}`)}</span>
                <span className="block text-[11.5px] leading-tight text-muted-foreground">
                  {PLATFORM_SHAPES[p as PlatformKey].map((s) => ratioLabel(s, picture)).join(' + ')}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      )}

      {purpose === 'picture' && (
        <div>
          <p className="mb-1.5 text-[13px] font-semibold text-foreground/85">{k('meShapes')}</p>
          <div className="flex flex-wrap gap-1.5">
            {FREE_SHAPES.map((s) => {
              const on = s === 'orig' ? !free.length || free.includes('orig') : free[0] === s;
              return (
                <button key={s} type="button" onClick={() => pickFree(s)} aria-pressed={on} data-testid={`qt-free-${s}`}
                  className={cn('min-h-[38px] rounded-full border px-3 text-[13px] font-semibold', on ? 'border-primary bg-primary/15' : 'border-white/10 bg-white/[0.04] text-foreground/80')}>
                  {s === 'orig' ? k('own', { ratio: ratioLabel('orig', picture) }) : ratioLabel(s)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {picture && purpose && (
        <div>
          <p className="mb-2 flex items-center gap-2 text-[13px] font-semibold text-foreground/85">
            {k(purpose === 'picture' ? 'yourPictureOne' : 'yourPicture')}
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" aria-label={k('working')} />}
          </p>
          <div className="grid grid-cols-2 gap-3">
            {shapes.map((s, i) => (
              <ShapeTile key={s} shape={s} main={i === 0} picture={picture} preview={previews?.[s] ?? null} place={places[s] ?? null}
                usedBy={usedBy(s)} onPlace={(p) => onPlace(s, p)} />
            ))}
          </div>
          {dropped.length > 0 && (
            <p className="mt-2 rounded-xl bg-white/[0.05] px-3 py-2 text-[12.5px] leading-snug text-foreground/80" data-testid="qt-shapes-dropped">
              {k('dropped', { shapes: dropped.map((s) => ratioLabel(s, picture)).join(', ') })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * One shape with the picture in it. Filled (the server's picture, the new room filled from the picture itself), or the
 * buyer's window: the picture cover-fit and dragged along the side that overflows, as the server will cut it.
 */
function ShapeTile({ shape, main, picture, preview, place, usedBy, onPlace }: {
  shape: ShapeKey; main: boolean; picture: { url: string; w: number; h: number }; preview: FramePreview['shapes'][string] | null;
  place: Place | null; usedBy: string[]; onPlace: (p: Place | null) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, opts?: Record<string, unknown>) => t(`qt.tools.canvas.shapes.${key}`, opts);
  const size = sizeOf(shape, picture);
  const box = size.w / size.h;
  const pic = picture.w / picture.h;
  // The automatic choice (the server's), unless the buyer chose.
  const auto = preview?.placed ?? null;
  const mode = place?.mode ?? auto?.mode ?? 'fill';
  const pos = place?.pos ?? (auto?.mode === 'crop' ? auto.pos : 0.5);
  const fits = Math.abs(box - pic) < 0.01;
  const wide = pic > box; // the picture overflows sideways when cut
  const drag = useRef<{ x: number; y: number; pos: number } | null>(null);
  const el = useRef<HTMLDivElement>(null);
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    const r = el.current?.getBoundingClientRect();
    if (!d || !r) return;
    // The overflow in screen pixels along the free side; moving the picture right shows more of its left.
    const over = wide ? r.height * pic - r.width : r.width / pic - r.height;
    if (!(over > 1)) return;
    const delta = wide ? e.clientX - d.x : e.clientY - d.y;
    onPlace({ mode: 'crop', pos: Math.max(0, Math.min(1, d.pos - delta / over)) });
  };
  const imgStyle: React.CSSProperties = wide
    ? { height: '100%', width: `${(pic / box) * 100}%`, left: `${-((pic / box) * 100 - 100) * pos}%`, top: 0 }
    : { width: '100%', height: `${(box / pic) * 100}%`, top: `${-((box / pic) * 100 - 100) * pos}%`, left: 0 };
  return (
    <div className="min-w-0" data-testid={`qt-shape-${shape}`}>
      <div
        ref={el}
        className={cn('relative w-full overflow-hidden rounded-xl bg-black ring-1', main ? 'ring-primary/60' : 'ring-white/10', mode === 'crop' && !fits && 'cursor-grab touch-none active:cursor-grabbing')}
        style={{ aspectRatio: `${size.w} / ${size.h}` }}
        onPointerDown={(e) => {
          if (mode !== 'crop' || fits) return;
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          drag.current = { x: e.clientX, y: e.clientY, pos };
        }}
        onPointerMove={onMove}
        onPointerUp={() => { drag.current = null; }}
        onPointerCancel={() => { drag.current = null; }}
      >
        {mode === 'crop' || fits ? (
          <img src={picture.url} alt="" draggable={false} className="pointer-events-none absolute max-w-none select-none" style={imgStyle} />
        ) : preview?.still ? (
          <img src={preview.still} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          // Until the server answers: the picture whole in the shape, the room around it dark.
          <img src={picture.url} alt="" className="absolute inset-0 h-full w-full object-contain opacity-60" />
        )}
        <span className="pointer-events-none absolute left-1.5 top-1.5 rounded-full bg-black/65 px-2 py-0.5 text-[11px] font-bold text-white">
          {ratioLabel(shape, picture)}{main ? ` · ${k('main')}` : ''}
        </span>
        {mode === 'crop' && !fits && (
          <span className="pointer-events-none absolute inset-x-1.5 bottom-1.5 flex items-center justify-center gap-1 rounded-full bg-black/65 px-2 py-0.5 text-[11px] font-semibold text-white">
            <Move className="h-3 w-3" aria-hidden />{k('drag')}
          </span>
        )}
      </div>
      {usedBy.length > 0 && <p className="mt-1 truncate text-[11.5px] text-muted-foreground">{usedBy.join(', ')}</p>}
      {!fits && (
        <div className="mt-1 grid grid-cols-2 gap-1" role="group" aria-label={k('howLabel')}>
          <button type="button" onClick={() => onPlace(auto?.mode === 'fill' ? null : { mode: 'fill', pos: 0.5 })} aria-pressed={mode === 'fill'} data-testid={`qt-shape-${shape}-fill`}
            className={cn('flex min-h-[34px] items-center justify-center gap-1 rounded-lg text-[11.5px] font-semibold', mode === 'fill' ? 'bg-white text-[#10131a]' : 'bg-white/[0.06] text-foreground/80')}>
            <Wand2 className="h-3.5 w-3.5" aria-hidden />{k('fill')}
          </button>
          <button type="button" onClick={() => onPlace({ mode: 'crop', pos })} aria-pressed={mode === 'crop'} data-testid={`qt-shape-${shape}-crop`}
            className={cn('flex min-h-[34px] items-center justify-center gap-1 rounded-lg text-[11.5px] font-semibold', mode === 'crop' ? 'bg-white text-[#10131a]' : 'bg-white/[0.06] text-foreground/80')}>
            <Move className="h-3.5 w-3.5" aria-hidden />{k('crop')}
          </button>
        </div>
      )}
      {auto?.mode === 'crop' && !place && <p className="mt-1 text-[11.5px] leading-snug text-amber-200/90">{k('subjectBoth')}</p>}
    </div>
  );
}
